import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import useConfrontationPlanningStyle, { couleursConfrontation } from "./ConfrontationPlanning.styles";
import { ConfrontationPlanningProps, ModeConfrontation, OptionConfrontation } from "./ConfrontationPlanning.types";
import { optionsConfrontation } from "./ConfrontationPlanning.utils";
import {
    useAgentsSelectionnables, useCircuitsPlanning, useDonneesPlanning, useFeriesEtVacances, useGroupesPlanning,
} from "./ConfrontationPlanning.states";
import { useSessionStore } from "../../lib/Store/sessionStore.component";
import { exporterPlanningEnPdf } from "./exporterPlanningPdf";
import FiltresPlanning from "./FiltresPlanning";
import GrillePlanning from "./GrillePlanning";
import DialogueSelectionAgents from "./DialogueSelectionAgents";

dayjs.extend(isoWeek);

// Attend deux rendus (rAF imbriqués) avant de résoudre, le temps que React commite
// et peigne le DOM sans la limite de hauteur du scroll interne de la grille
const attendreProchainRendu = () => new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
});

export const ConfrontationPlanning: FunctionComponent<ConfrontationPlanningProps> = () => {
    const { styles, isResponsive } = useConfrontationPlanningStyle();
    const { currentLogin, currentEntite } = useSessionStore.getState().getCurrentInfos();

    const [mode, setMode] = useState<ModeConfrontation>('service');
    const [option, setOption] = useState<OptionConfrontation | undefined>(optionsConfrontation('service', currentLogin)[0]);
    const [dateDebut, setDateDebut] = useState<Dayjs>(() => dayjs().startOf('isoWeek'));
    const [dateFin, setDateFin] = useState<Dayjs>(() => dayjs().startOf('isoWeek').add(6, 'day'));
    const [plageDebut, setPlageDebut] = useState('08:00');
    const [plageFin, setPlageFin] = useState('18:00');
    const [afficherPresences, setAfficherPresences] = useState(true);
    // Mode "autre" : agents cochés dans DialogueSelectionAgents (identifiants)
    const [agentsSelectionnes, setAgentsSelectionnes] = useState<number[]>([]);
    const [dialogueAgentsOuverte, setDialogueAgentsOuverte] = useState(false);
    // Compteur incrémenté par le bouton "rafraîchir", pour forcer une nouvelle génération des données temporaires
    const [version, setVersion] = useState(0);
    const [exportPdfEnCours, setExportPdfEnCours] = useState(false);
    const grilleRef = useRef<View>(null);

    const { feries, vacances, toggleVacances } = useFeriesEtVacances(dateDebut, dateFin);
    const groupesPlanning = useGroupesPlanning(currentLogin?.droitsPlanning, currentLogin?.identifiant);
    const circuitsPlanning = useCircuitsPlanning(currentLogin?.identifiant);
    const agentsDisponibles = useAgentsSelectionnables(
        mode === 'autre',
        currentLogin?.identifiant,
        dateDebut,
        dateFin,
        currentEntite?.identifiantEntite,
        currentEntite?.collectivitegr?.afficherElus === 1,
    );

    const options = useMemo(() => {
        if(mode === 'groupe') return groupesPlanning;
        if(mode === 'circuit') return circuitsPlanning;
        return optionsConfrontation(mode, currentLogin);
    }, [mode, currentLogin, groupesPlanning, circuitsPlanning]);

    const handleChangeMode = useCallback((nouveauMode: ModeConfrontation) => {
        setMode(nouveauMode);
        if(nouveauMode === 'groupe') setOption(groupesPlanning[0]);
        else if(nouveauMode === 'circuit') setOption(circuitsPlanning[0]);
        else if(nouveauMode === 'autre') setDialogueAgentsOuverte(true);
        else setOption(optionsConfrontation(nouveauMode, currentLogin)[0]);
    }, [currentLogin, groupesPlanning, circuitsPlanning]);

    // Les groupes/circuits se chargent de façon asynchrone : si leur mode est déjà actif
    // quand la réponse arrive, on sélectionne la première option une fois disponible
    useEffect(() => {
        if(mode === 'groupe' && groupesPlanning.length > 0){
            setOption((actuelle) => actuelle && groupesPlanning.some((groupe) => groupe.identifiant === actuelle.identifiant)
                ? actuelle
                : groupesPlanning[0]);
        }
    }, [mode, groupesPlanning]);

    useEffect(() => {
        if(mode === 'circuit' && circuitsPlanning.length > 0){
            setOption((actuelle) => actuelle && circuitsPlanning.some((circuit) => circuit.identifiant === actuelle.identifiant)
                ? actuelle
                : circuitsPlanning[0]);
        }
    }, [mode, circuitsPlanning]);

    const handleSemainePrecedente = useCallback(() => {
        const dureeJours = dateFin.diff(dateDebut, 'day') + 1;
        setDateDebut((date) => date.subtract(dureeJours, 'day'));
        setDateFin((date) => date.subtract(dureeJours, 'day'));
    }, [dateDebut, dateFin]);

    const handleSemaineSuivante = useCallback(() => {
        const dureeJours = dateFin.diff(dateDebut, 'day') + 1;
        setDateDebut((date) => date.add(dureeJours, 'day'));
        setDateFin((date) => date.add(dureeJours, 'day'));
    }, [dateDebut, dateFin]);

    const handleExporterPdf = useCallback(async () => {
        if(exportPdfEnCours) return;
        setExportPdfEnCours(true);
        try {
            // `pourImpression` (passé à GrillePlanning) retire la hauteur de défilement limitée de la
            // grille le temps de la capture ; on attend le rendu correspondant avant de capturer le DOM
            await attendreProchainRendu();
            const noeud = grilleRef.current as unknown as HTMLElement | null;
            if(noeud) await exporterPlanningEnPdf(noeud, `planning-semaine-${dateDebut.isoWeek()}.pdf`);
        } finally {
            setExportPdfEnCours(false);
        }
    }, [dateDebut, exportPdfEnCours]);

    // Agents + présences/absences/demandes via PlanningConfrontationApi ; `version` force un
    // rechargement au clic sur "rafraîchir" sans être utilisé par le calcul lui-même
    const donnees = useDonneesPlanning(mode, option, dateDebut, dateFin, version, agentsSelectionnes);

    return (
        <ScrollView contentContainerStyle={styles.container}>
            <View style={styles.entete}>
                <Text style={styles.titre}>Planning</Text>
                <Pressable
                    style={[styles.boutonExporterPdf, exportPdfEnCours && styles.boutonExporterPdfDesactive]}
                    onPress={handleExporterPdf}
                    disabled={exportPdfEnCours}
                    testID="BT_EXPORTER_PDF_PLANNING"
                >
                    <MaterialCommunityIcons name="file-pdf-box" size={18} color={couleursConfrontation.enteteTexte} />
                    <Text style={styles.boutonExporterPdfTexte}>
                        {exportPdfEnCours ? 'Génération du PDF…' : 'Exporter en PDF'}
                    </Text>
                </Pressable>
            </View>
            <FiltresPlanning
                mode={mode}
                onChangeMode={handleChangeMode}
                option={option}
                options={options}
                onChangeOption={setOption}
                dateDebut={dateDebut}
                dateFin={dateFin}
                onChangeDateDebut={setDateDebut}
                onChangeDateFin={setDateFin}
                onRafraichir={() => setVersion((v) => v + 1)}
                vacances={vacances}
                onToggleVacances={toggleVacances}
                avecGroupes={groupesPlanning.length > 0}
                avecCircuits={circuitsPlanning.length > 0}
                nombreAgentsSelectionnes={agentsSelectionnes.length}
                onOuvrirSelectionAgents={() => setDialogueAgentsOuverte(true)}
                plageDebut={plageDebut}
                plageFin={plageFin}
                onChangePlageDebut={setPlageDebut}
                onChangePlageFin={setPlageFin}
                afficherPresences={afficherPresences}
                onToggleAfficherPresences={() => setAfficherPresences((valeur) => !valeur)}
                styles={styles}
            />
            <GrillePlanning
                ref={grilleRef}
                pourImpression={exportPdfEnCours}
                agents={donnees.agents}
                donnees={donnees}
                feries={feries}
                vacances={vacances}
                dateDebut={dateDebut}
                dateFin={dateFin}
                numeroSemaine={dateDebut.isoWeek()}
                onSemainePrecedente={handleSemainePrecedente}
                onSemaineSuivante={handleSemaineSuivante}
                plageDebut={plageDebut}
                plageFin={plageFin}
                afficherPresences={afficherPresences}
                styles={styles}
            />
            {dialogueAgentsOuverte && (
                <DialogueSelectionAgents
                    agents={agentsDisponibles}
                    selectionInitiale={agentsSelectionnes}
                    isResponsive={isResponsive}
                    onValider={(selection) => {
                        setAgentsSelectionnes(selection);
                        setDialogueAgentsOuverte(false);
                    }}
                    onClose={() => setDialogueAgentsOuverte(false)}
                    styles={styles}
                />
            )}
        </ScrollView>
    );
}

export default ConfrontationPlanning;
